import { applySnapshot, cast, Instance, types } from 'mobx-state-tree';
import { transl } from '../main/transl';
import { mg } from '../mg/mg';
import { DataviewStoreColumn, DataviewStoreColumnHider, IDataviewStoreColumn, newDataviewStoreColumn } from './DataviewStoreColumn';
import { getOmnistore } from './Omnistore';

const DataviewStore_Sorting = types
	.model('DataviewStore_Sorting', {
		column: 'id',
		reversed: false,
	})
	.actions(self => ({
		columnClick(column) {
			if (!column.isSortable) return;
			const columnName = column.name;
			if (columnName === self.column) {
				self.reversed = !self.reversed;
			} else {
				self.column = columnName;
				self.reversed = false;
			}
		},
	}));

export const DataviewStore_Filtering = types
	.model({
		columns: types.array(types.string),
		value: '',
		type: types.optional(types.enumeration(['NORMAL', 'SELECT', 'ENUM', 'BOOLEAN']), 'NORMAL'),
		_label: types.maybe(types.string),
	})
	.views(self => ({
		get label() {
			return self._label!==undefined ? self._label : self.columns.map(c => transl(c)).join(' / ');
		},
	}));

export const DataviewStore = types
	.model('DataviewStore', {
		id: types.identifier,
		orgColumns: types.array(types.string),
		sorting: types.optional(DataviewStore_Sorting, {}),
		filtering: types.array(DataviewStore_Filtering),
		disabling: '',
		highlighting: '',
		isShowingSubmenu: types.maybe(types.boolean), //TODO consider dropping
		isShowingFiltering: true,
		rowWidth: 0,
		selectedEntityId: 0,
		isShowingMultiselection: false,
		_multiselectedEntityIds: types.map(types.boolean),
		_ignoredColumnHiders: types.array(types.string),
		_columns: types.array(DataviewStoreColumn),
	})
	.views(self => ({
		get _ignoredHidders() : Array<string> {
			const o = getOmnistore();
			const show = o.reoctoStore.isTester || o.authStore.role==='ADMIN';
			return show ? ['HIDDEN'] : [];
		},
	}))
	.actions(self => ({
		afterCreate() {
			applySnapshot(self._columns, self.orgColumns.map(newDataviewStoreColumn));
		},
	}))
	.actions(self => ({
		setIgnoredColumnHiders(ignoredColumnHiders: Array<Instance<typeof DataviewStoreColumnHider> | null>) {
			const a: Array<Instance<typeof DataviewStoreColumnHider>> = ignoredColumnHiders.filter(r => r) as any; //TODO
			if (!mg.shallowEqual(a, self._ignoredColumnHiders)) self._ignoredColumnHiders = cast(a);
		},
		addColumnHiderToNamed(reason: Instance<typeof DataviewStoreColumnHider>, ...names) {
			self._columns.forEach(c => names.indexOf(c.name) > -1 && c.addColumnHider(reason));
		},
		hideAllButListed(...names: Array<string>) {
			self._columns.forEach(c => names.indexOf(c.name) == -1 && c.addColumnHider('DEFAULT'));
		},
		columnClick(column) {
			self.sorting.columnClick(column);
		},
		rowClick(selectedEntityId) {
			self.selectedEntityId = selectedEntityId;
		},
		setRowWidth(rowWidth: number) {
			self.rowWidth = rowWidth;
		},
		setFirstFilteringValue(value: string) { //TODO
			self.filtering[0].value = value;
		},
		setFilteringValue(index: number, value: string) {
			self.filtering[index].value = value;
		},
		clearFiltering() {
			self.filtering.forEach(f => f.value='');
		},
		toggleIsShowingSubmenu() {
			self.isShowingSubmenu = !self.isShowingSubmenu;
		},
		addMultiselected(id: string | number) {
			self._multiselectedEntityIds.set('' + id, true);
		},
		removeMultiselected(id: string | number) {
			self._multiselectedEntityIds.delete('' + id);
		},
		toggleMultiselected(id: string | number) {
			const m = self._multiselectedEntityIds;
			const k = '' + id;
			if (m.get(k)) {
				m.delete(k);
			} else {
				m.set(k, true);
			}
		},
		clearMultiselected() {
			self._multiselectedEntityIds.clear();
		},
	}))
	.views(self => ({
		columnByName(name: string, def?: IDataviewStoreColumn) : IDataviewStoreColumn {
			const r = self._columns.filter(c => c.name===name)[0];
			if (r) return r;
			if (def !== undefined) return def;
			throw new Error('No such column "' + name + '" in "' + self.id + '"');
		},
		isMultiselected(id: string | number) {
			return !!self._multiselectedEntityIds.get('' + id);
		},
		get nMultiselected() {
			return self._multiselectedEntityIds.size;
		},
		get allMultiselectedIds() : Set<number> {
			const result = new Set<number>();
			for (let s of self._multiselectedEntityIds.keys()) result.add(+s);
			return result;
		},
	}))
	.views(self => ({
		get visibleColumns() {
			const reasons = [...self._ignoredHidders, ...self._ignoredColumnHiders];
			//console.log('visibleColumns', reasons)
			return self._columns.filter(c => c.isVisibleIgnoring(reasons));
		},
		get availableColumns() {
			const reasons = [...self._ignoredHidders, ...self._ignoredColumnHiders, 'DEFAULT'];
			//console.log('availableColumns', reasons)
			return self._columns.filter(c => c.isVisibleIgnoring(reasons));
		},
	}))
	.views(self => ({
		get widthDescSum() {
			return self.visibleColumns.map(c => c.widthDesc).reduce((a, b) => a+b, 0);
		},
	}))
	.views(self => ({
		get unitWidth() {
			return self.rowWidth / self.widthDescSum;
		},
		get _compareFn() {
			return self.columnByName(self.sorting.column).comparator(self.sorting.reversed);
		},
	}))
	.views(self => ({
		applyTo<E>(list: Array<E>) : Array<E> {
			const matches = e => {
				if (self.isMultiselected(e.id)) return true;
				for (let f of self.filtering) {
					let ok = !f.value;
					if (ok) continue;
					for (let c of f.columns) {
						let v = e[c];
						if (v===undefined || v===null || v==='') continue;
						switch (f.type) {
						case 'NORMAL':
							v = typeof v === 'string' ? v.toLowerCase() : '' + v;
							ok = v.indexOf(f.value.toLowerCase()) !== -1;
							break;
						case 'ENUM':
							ok = v == f.value;
							break;
						case 'SELECT':
							ok = v == f.value;
							break;
						case 'BOOLEAN':
							ok = v == (f.value === 'true');
							console.log(typeof v, v, 'ffff', typeof f.value, f.value);
							break;
						}
						if (ok) break;
					}
					if (!ok) return false;
				}
				return true;
			};
			const result = list.filter(matches);
			result.sort(self._compareFn);
			return result;
		},
	}));

export type IDataviewStore = Instance<typeof DataviewStore>
